import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import documentsService from '../../services/documentsService';

const DownloadsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentsService.getActiveDocuments();
      setDocuments(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      setDownloading(prev => ({ ...prev, [doc.id]: true }));
      
      // Simple download - let the service handle S3 redirect
      await documentsService.downloadDocument(doc.id, doc.originalName);
      toast.success('Download started!');
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return '📊';
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return '📋';
    if (mimeType?.includes('text')) return '📃';
    return '📁';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Downloads</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Access important documents, forms, and resources for East End Prep Sports.
          </p>
        </div>

        {/* Simple Documents List */}
        {documents.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📁</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Documents Available</h3>
            <p className="text-gray-600">Check back later for important documents and resources.</p>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="divide-y divide-gray-200">
                {documents.map((document) => (
                  <div key={document.id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="text-2xl flex-shrink-0">
                          {getFileIcon(document.mimeType)}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {document.title}
                          </h3>
                          {document.description && (
                            <p className="text-sm text-gray-700 mt-1 mb-2">
                              {document.description}
                            </p>
                          )}
                          <p className="text-sm text-gray-500">
                            {document.originalName}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleDownload(document)}
                        disabled={downloading[document.id]}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200 flex items-center space-x-2 min-w-[120px] justify-center"
                      >
                        {downloading[document.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Downloading...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>Download</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default DownloadsPage;